import { PaginatedTable } from '@avanta/shared/components/PaginatedTable';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { formatEUR } from '@avanta/shared/utils/currency';
import { formatDateSimple } from '@avanta/shared/utils/date';
import { type ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { PageHeader } from 'components/PageHeader';
import { EMPTY_VALUE } from 'constants/common';
import { TableActions } from './TableActions';
import { useDownloadInvoicePdf } from './TableActions/useDownloadInvoicePdf';
import { type BillingInvoice } from './types';
import { useFetchInvoices } from './useFetchInvoices';

const getTableColumns = (
  onDownload: (invoice: BillingInvoice) => void,
  isDownloadingInvoice: (invoiceId: number) => boolean
): ColumnDef<BillingInvoice>[] => [
  {
    id: 'invoiceNumber',
    header: t('BillingPage.Table.invoiceNumber.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorKey: 'number',
    enableSorting: true,
  },
  {
    id: 'closeDate',
    header: t('BillingPage.Table.closeDate.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorKey: 'invoiceDate',
    enableSorting: true,
    cell: ({ row }): string => formatDateSimple(row.original.invoiceDate, 'yyyy-MM-dd'),
  },
  {
    id: 'amount',
    header: t('BillingPage.Table.amount.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorKey: 'totalAmount',
    cell: ({ row }): string =>
      row.original.totalAmount === null ? EMPTY_VALUE : formatEUR(row.original.totalAmount),
  },
  {
    id: 'actions',
    cell: ({ row }): React.ReactElement => (
      <TableActions
        invoice={row.original}
        onDownload={onDownload}
        isPending={isDownloadingInvoice(row.original.id)}
      />
    ),
  },
];

export const Billing = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const { downloadPdf, isPending, pendingInvoiceId } = useDownloadInvoicePdf();
  const columns = getTableColumns(
    downloadPdf,
    invoiceId => isPending && pendingInvoiceId === invoiceId
  );
  const { table, selectedDateRange, setDateRange } = useFetchInvoices(columns);

  return (
    <div className="flex flex-col gap-8">
      <PageHeader title={t('BillingPage.heading')} description={t('BillingPage.description')} />
      <PaginatedTable<BillingInvoice>
        autoPageSize
        table={table}
        columns={columns}
        dateRangeFilter={{ accessorKey: 'invoiceDate' }}
        selectedDateRange={selectedDateRange}
        setDateRange={setDateRange}
        searchPlaceholder="BillingPage.search.placeholder"
        searchPlaceholderNs={LOCIZE_NS.INSURANCE}
        emptyTitle={t('BillingPage.empty')}
        tableClassName="w-full"
        withSearch
        onRowAction={downloadPdf}
      />
    </div>
  );
};
